'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, useFollow, useGetFollowsCheck, useUnfollow } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type JSX, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { TOAST_IDS } from '@/config/toast-ids';
import { showToast } from '@/lib/ui/toast';
interface FollowButtonProps {
  targetUserId: string;
  /**
   * @deprecated The v2 mutation manifest re-fetches the follows-check cache
   * automatically via `optimistic.invalidate: true`. This prop is kept on
   * the API for backwards compatibility but is no longer required — callers
   * may drop it once their consumers stop relying on the synchronous hook.
   */
  onFollowChange?: () => void;
}

/**
 * Follow / Unfollow button for another user's profile.
 *
 * Reads the viewer→target follow state via `useGetFollowsCheck` and toggles
 * it through `useFollow` / `useUnfollow`. The v2 mutations are optimistic
 * with automatic cache invalidation declared in `sdk.manifest.yml`, so no
 * manual `useState` mirror or `try/catch` rollback is required at the
 * call site.
 */
export function FollowButton(t0) {
  const $ = _c(27);
  const {
    targetUserId,
    onFollowChange
  } = t0;
  const t = useTranslations("profile.follow");
  const {
    user,
    isAuthenticated
  } = useAuth();
  const [isHovered, setIsHovered] = useState(false);
  const isOwn = isAuthenticated && user !== null && user.id === targetUserId;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["isFollowing"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== targetUserId) {
    t2 = {
      userId: targetUserId,
      kind: "check",
      fields: t1
    };
    $[1] = targetUserId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const followCheck = useGetFollowsCheck(t2);
  const follow = useFollow();
  const unfollow = useUnfollow();
  if (!isAuthenticated || isOwn || followCheck.isPending) {
    return null;
  }
  const isFollowing = followCheck.data?.isFollowing === true;
  const isPending = follow.isPending || unfollow.isPending;
  let t3;
  if ($[3] !== follow || $[4] !== isFollowing || $[5] !== isPending || $[6] !== onFollowChange || $[7] !== t || $[8] !== targetUserId || $[9] !== unfollow) {
    t3 = function handleToggle() {
      if (isPending) {
        return;
      }
      if (isFollowing) {
        showToast({
          id: TOAST_IDS.UNFOLLOW_ACTION,
          message: t("unfollow_loading"),
          variant: "loading"
        });
        unfollow.mutate({
          userId: targetUserId
        }, {
          onSuccess: () => {
            showToast({
              id: TOAST_IDS.UNFOLLOW_ACTION,
              message: t("unfollow_success"),
              variant: "success"
            });
            onFollowChange?.();
          },
          onError: () => {
            showToast({
              id: TOAST_IDS.FOLLOW_ERROR,
              message: t("follow_error"),
              variant: "error"
            });
          }
        });
      } else {
        showToast({
          id: TOAST_IDS.FOLLOW_ACTION,
          message: t("follow_loading"),
          variant: "loading"
        });
        follow.mutate({
          userId: targetUserId
        }, {
          onSuccess: () => {
            showToast({
              id: TOAST_IDS.FOLLOW_ACTION,
              message: t("follow_success"),
              variant: "success"
            });
            onFollowChange?.();
          },
          onError: () => {
            showToast({
              id: TOAST_IDS.FOLLOW_ERROR,
              message: t("follow_error"),
              variant: "error"
            });
          }
        });
      }
    };
    $[3] = follow;
    $[4] = isFollowing;
    $[5] = isPending;
    $[6] = onFollowChange;
    $[7] = t;
    $[8] = targetUserId;
    $[9] = unfollow;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleToggle = t3;
  let t4;
  if ($[11] !== isFollowing || $[12] !== isHovered || $[13] !== t) {
    t4 = function getLabel() {
      if (!isFollowing) {
        return t("follow_button");
      }
      if (isHovered) {
        return t("unfollow_button");
      }
      return t("following_button");
    };
    $[11] = isFollowing;
    $[12] = isHovered;
    $[13] = t;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const getLabel = t4;
  let t5;
  if ($[15] !== isFollowing || $[16] !== isHovered) {
    t5 = function getVariant() {
      if (!isFollowing) {
        return "primary";
      }
      if (isHovered) {
        return "destructive";
      }
      return "secondary";
    };
    $[15] = isFollowing;
    $[16] = isHovered;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const getVariant = t5;
  const t6 = getVariant();
  let t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setIsHovered(true);
    };
    t8 = () => {
      setIsHovered(false);
    };
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  let t9;
  if ($[20] !== getLabel) {
    t9 = getLabel();
    $[20] = getLabel;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== handleToggle || $[23] !== isPending || $[24] !== t6 || $[25] !== t9) {
    t10 = <AnimatedButton variant={t6} size="sm" disabled={isPending} loading={isPending} onClick={handleToggle} onPointerEnter={t7} onPointerLeave={t8} className="min-w-[5.5rem]">{t9}</AnimatedButton>;
    $[22] = handleToggle;
    $[23] = isPending;
    $[24] = t6;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}